<template>
  <div class="rt-xpc-backtop rt-xpc-backtop-transition">
    <rt-tooltip
      :disabled="!tooltip"
      :content="tooltip"
      effect="dark"
      placement="left"
    >
      <transition :name="viewTransition">
        <div
          v-show="visible"
          :style="{ bottom: styleBottom, right: styleRight }"
          :class="backtopClasses"
          class="rt-xpc-backtop-content"
          @click.stop="handleClick"
        >
          <span
            v-if="shape === 'square' && type === 'text'"
            :class="colorClasses"
            class="rt-xpc-backtop-color"
          >{{
            text
          }}</span>
          <rt-icon
            v-else
            :name="icon"
            :class="colorClasses"
            :color="computedIconColor"
            class="rt-xpc-backtop-color"
            size="24"
          ></rt-icon>
        </div>
      </transition>
    </rt-tooltip>
  </div>
</template>

<script>
import throttle from 'throttle-debounce/throttle';
const cubic = value => Math.pow(value, 3);
const easeInOutCubic = value => (value < 0.5 ? cubic(value * 2) / 2 : 1 - cubic((1 - value) * 2) / 2);
export default {
  name: 'RtBacktop',
  props: {
    target: [String],
    // 配置文字
    text: {
      type: String,
      default: 'UP'
    },
    // 类型（文字、图标）
    type: {
      type: String,
      default: 'text'
    },
    // 距离页面底部下边距
    bottom: {
      type: Number,
      default: 40
    },
    // 距离页面右边距
    right: {
      type: Number,
      default: 40
    },
    // 出现回到顶部按钮的滚动高度
    scrollHeight: {
      type: Number,
      default: 200
    },
    // 图标
    icon: {
      type: String,
      default: 'rt-base-top-lined'
    },
    // 字体、图标颜色
    color: String,
    // 背景色
    bgColor: String,

    // 阴影
    noShadow: {
      type: Boolean,
      default: false
    },
    // 形状
    shape: {
      type: String,
      default: 'round',
      validator: val => {
        return [
          'round',
          'square' //
        ].includes(val);
      }
    },
    // 圆角度
    radius: {
      type: String,
      default: 'medium'
    },
    // tooltip文字提示内容
    tooltip: String
  },

  data () {
    return {
      el: null,
      container: null,
      visible: false
    };
  },

  computed: {
    viewTransition () {
      return 'rt-xpc-backtop-' + (this.visible ? 'in' : 'out');
    },
    styleBottom () {
      return `${this.bottom + 20}px`;
    },
    styleRight () {
      return `${this.right + 20}px`;
    },
    computedIconColor () {
      if (this.computedColor === 'neutralPrimary') {
        return this.SCSS_VARIABLES.NEUTRAL_COLOR_PRIMARY;
      } else if (this.computedColor === 'neutralRegular') {
        return this.SCSS_VARIABLES.NEUTRAL_COLOR_REGULAR;
      } else if (this.computedColor === 'neutralSecondary') {
        return this.SCSS_VARIABLES.NEUTRAL_COLOR_SECONDARY;
      } else if (this.computedColor === 'primary') {
        return this.SCSS_VARIABLES.COLOR_INFO;
      } else if (this.computedColor === 'normal') {
        return this.SCSS_VARIABLES.COLOR_INFO;
      } else if (this.computedColor === 'success') {
        return this.SCSS_VARIABLES.COLOR_SUCCESS;
      } else if (this.computedColor === 'warning') {
        return this.SCSS_VARIABLES.WARNING;
      } else if (this.computedColor === 'danger') {
        return this.SCSS_VARIABLES.COLOR_DANGER;
      } else if (this.computedColor === 'info') {
        return this.SCSS_VARIABLES.COLOR_INFO;
      } else if (this.computedColor === 'white') {
        return this.SCSS_VARIABLES.COLOR_TEXT_WHITE;
      } else {
        return ''
      }
    },
    backtopClasses () {
      let classList = [];
      if (this.computedBgColor) {
        classList.push('rt-xpc-backtop-bgcolor-' + this.computedBgColor);
      }
      if (this.computedColor) {
        classList.push('rt-xpc-backtop-' + this.computedColor);
      }
      if (this.noShadow) {
        classList.push('rt-xpc-backtop-noshadow');
      }
      if (this.shape) {
        classList.push('rt-xpc-backtop-' + this.shape);
      }
      if (this.shape === 'square') {
        classList.push('rt-xpc-backtop-radius-medium');
        if (this.radius) {
          classList.push('rt-xpc-backtop-radius-' + this.radius);
        }
      }
      if (this.tooltip) {
        classList.push('rt-xpc-backtop-tooltip');
      }
      return classList;
    },
    colorClasses () {
      let classList = [];
      if (this.computedColor) {
        classList.push('rt-xpc-backtop-color-' + this.computedColor);
      }
      return classList;
    },
    defaultColor () {
      return this.shape === 'round' ? 'neutralPrimary' : 'white';
    },
    defaultBgColor () {
      return this.shape === 'round' ? 'white' : 'primary';
    },
    computedColor () {
      if (this.color) {
        return this.color;
      } else {
        return this.defaultColor;
      }
    },
    computedBgColor () {
      if (this.bgColor) {
        return this.bgColor;
      } else {
        return this.defaultBgColor;
      }
    }
  },
  mounted () {
    this.init();
    this.throttledScrollHandler = throttle(300, this.onScroll);
    this.container.addEventListener('scroll', this.throttledScrollHandler);
  },
  beforeDestroy () {
    this.container.removeEventListener('scroll', this.throttledScrollHandler);
  },
  methods: {
    init () {
      this.container = document;
      this.el = document.documentElement;
      if (this.target) {
        this.el = document.querySelector(this.target);
        if (!this.el) {
          throw new Error(`target is not existed: ${this.target}`);
        }
        this.container = this.el;
      }
    },
    onScroll () {
      const scrollTop = this.el.scrollTop;
      this.visible = scrollTop >= this.scrollHeight;
    },
    handleClick (e) {
      this.scrollToTop();
      this.$emit('click', e);
    },
    scrollToTop () {
      const el = this.el;
      const beginTime = Date.now();
      const beginValue = el.scrollTop;
      const rAF = window.requestAnimationFrame || (func => setTimeout(func, 16));
      const frameFunc = () => {
        const progress = (Date.now() - beginTime) / 500;
        if (progress < 1) {
          el.scrollTop = beginValue * (1 - easeInOutCubic(progress));
          rAF(frameFunc);
        } else {
          el.scrollTop = 0;
        }
      };
      rAF(frameFunc);
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtBacktop.vue?a001dc80